import { useCallback } from 'react';

import type { InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useFetchDetailedInboxEntryNetworkOnly } from '../../services/inboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { clearOptimisticMove } from '../../utils/optimisticMoveGuard';

interface ReconcileFailedMoveParams {
  inboxEntryId: string;
  requestedTaskSubgroupId: string | null;
  capturedTaskGroupId: string | null;
  revert: () => void;
  isCurrent?: () => boolean;
}

interface UseReconcileFailedMoveResult {
  reconcileFailedMove: (params: ReconcileFailedMoveParams) => Promise<boolean>;
}

export const useReconcileFailedMove = (): UseReconcileFailedMoveResult => {
  const { fetchDetailedInboxEntry } = useFetchDetailedInboxEntryNetworkOnly();

  const fetchServerEntry = useCallback(
    async (inboxEntryId: string): Promise<InboxEntry | null> => {
      try {
        return await fetchDetailedInboxEntry(inboxEntryId);
      } catch (error) {
        log.error('Failed to fetch inbox entry for move reconciliation', error);
        return null;
      }
    },
    [fetchDetailedInboxEntry],
  );

  const reconcileFailedMove = useCallback(
    async ({
      inboxEntryId,
      requestedTaskSubgroupId,
      capturedTaskGroupId,
      revert,
      isCurrent,
    }: ReconcileFailedMoveParams): Promise<boolean> => {
      const serverEntry = await fetchServerEntry(inboxEntryId);
      if (isCurrent && !isCurrent()) {
        return false;
      }
      clearOptimisticMove(inboxEntryId);

      const activeTaskGroup =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      if (activeTaskGroup.taskGroupId !== capturedTaskGroupId) {
        return false;
      }
      if (!serverEntry) {
        revert();
        return false;
      }
      activeTaskGroup.updateInboxEntry(serverEntry);
      return (serverEntry.taskSubgroupId ?? null) === requestedTaskSubgroupId;
    },
    [fetchServerEntry],
  );

  return { reconcileFailedMove };
};
